import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "328",
  "slug": "highest-mountains-in-the-world-ranked",
  "title": "The 10 Highest Mountains in the World, Ranked (And Why Everest Isn't the Tallest)",
  "excerpt": "All ten of the world's highest peaks are in the Himalaya and Karakoram, and every one clears 8,000 metres. Here they are ranked by elevation — plus why 'highest' and 'tallest' are two different contests Everest doesn't both win.",
  "date": "2026-06-02",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1483921020237-2ff51e8e4b22?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzbm93eSUyMG1vdW50YWluJTIwcGVha3N8ZW58MXwwfHx8MTc4MDM5MzI3NHww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "There are only fourteen mountains on Earth that rise above 8,000 metres — climbers call them the eight-thousanders — and the ten highest are all clustered in two mountain ranges on the same side of the planet. Here they are by elevation above sea level, followed by the reason Everest, for all its fame, doesn't actually win every 'biggest mountain' contest."
    },
    {
      "type": "heading",
      "text": "The 10 Highest Mountains by Elevation Above Sea Level"
    },
    {
      "type": "list",
      "items": [
        "1. Mount Everest — 8,849 m. On the Nepal–China (Tibet) border; the highest point on Earth above sea level.",
        "2. K2 — 8,611 m. In the Karakoram on the Pakistan–China border; far more dangerous to climb than Everest.",
        "3. Kangchenjunga — 8,586 m. On the India–Nepal border; the highest mountain in India.",
        "4. Lhotse — 8,516 m. Directly connected to Everest by the South Col.",
        "5. Makalu — 8,485 m. A striking four-sided pyramid near Everest.",
        "6. Cho Oyu — 8,188 m. Considered the most accessible of the eight-thousanders.",
        "7. Dhaulagiri I — 8,167 m. In central Nepal; its name means 'dazzling white mountain'.",
        "8. Manaslu — 8,163 m. In the Nepalese Himalaya; the name means 'mountain of the spirit'.",
        "9. Nanga Parbat — 8,126 m. The western anchor of the Himalaya, in Pakistan; nicknamed 'Killer Mountain'.",
        "10. Annapurna I — 8,091 m. In Nepal; statistically one of the deadliest major peaks to climb."
      ]
    },
    {
      "type": "heading",
      "text": "Highest vs. Tallest vs. Farthest From the Centre"
    },
    {
      "type": "paragraph",
      "text": "Everest is the highest mountain, but 'highest' only means highest above sea level. Change the question and the winner changes too."
    },
    {
      "type": "list",
      "items": [
        "Tallest, base to summit: Mauna Kea in Hawaii. Measured from its base on the ocean floor to its peak, it stands over 10,000 metres — taller than Everest — but most of it is underwater, so only about 4,200 metres pokes above the sea.",
        "Farthest point from Earth's centre: Mount Chimborazo in Ecuador. Because the Earth bulges at the equator, Chimborazo's summit sits farther from the planet's core than Everest's, even though it's a lower elevation. Standing on Chimborazo, you're the closest a person can get to outer space on solid ground.",
        "Highest above sea level: Mount Everest. The classic answer, and the one almost everyone means."
      ]
    },
    {
      "type": "heading",
      "text": "How Do You Even Measure a Mountain?"
    },
    {
      "type": "paragraph",
      "text": "Pinning down a peak's exact height is harder than it sounds, because you first have to agree on where 'sea level' is beneath a mountain hundreds of kilometres from any ocean. Surveyors use a model of the Earth's gravity field called the geoid as their zero line. Everest's official figure has shifted over the decades as techniques improved — and in December 2020, China and Nepal jointly announced a new, agreed elevation of 8,848.86 metres after fresh surveys using satellite positioning and ground measurements, settling a long-standing minor disagreement between the two countries."
    },
    {
      "type": "heading",
      "text": "Why They're All in One Place"
    },
    {
      "type": "paragraph",
      "text": "It's no coincidence that all ten of the world's highest peaks line up across the Himalaya and Karakoram. Around 50 million years ago, the Indian tectonic plate collided with the Eurasian plate and never stopped pushing. With nowhere to go but up, the crust crumpled into the highest mountains on the planet — and the collision is still going, so the Himalaya are still slowly rising today, by a few millimetres a year. The same geological event that built Everest built every peak on this list."
    },
    {
      "type": "heading",
      "text": "What Makes a Mountain Deadly"
    },
    {
      "type": "paragraph",
      "text": "Height and danger aren't the same thing. Everest is the highest, but K2 and Annapurna are far deadlier to climb, with much higher fatality rates relative to the number of summits. Steep technical terrain, unpredictable weather, avalanche-prone slopes, and the thin air of the 'death zone' above 8,000 metres matter more than raw elevation. It's a useful reminder that the most impressive number isn't always the most important one."
    },
    {
      "type": "heading",
      "text": "A Different List: The Seven Summits"
    },
    {
      "type": "paragraph",
      "text": "The ten highest peaks are a Himalayan affair, but there's a famous alternative challenge that spreads the load across the globe: the Seven Summits, the highest mountain on each of the seven continents. The list pairs Everest (Asia) with Aconcagua in Argentina (South America, about 6,961 m), Denali in Alaska (North America, about 6,190 m), Kilimanjaro in Tanzania (Africa, about 5,895 m), Mount Elbrus in Russia (Europe, about 5,642 m), the Vinson Massif (Antarctica, about 4,892 m), and either Australia's Mount Kosciuszko or New Guinea's Puncak Jaya for Oceania, depending on how you draw the region. Climbing all seven has become a mountaineering rite of passage."
    },
    {
      "type": "paragraph",
      "text": "High mountains have unmistakable signatures from a satellite's view: long shadows cast by sharp peaks, snow and ice clinging to summits and slopes, deep V-shaped valleys, and the absence of roads and towns at altitude. Recognising a mountain range and its orientation is one of the fastest ways to narrow down a location. Want to train your eye? EarthGuessr drops you onto real terrain from around the world — read the ridges and see if you can place yourself."
    }
  ]
};
